/**
 * One department per cart.
 *
 * Bodyboard and Pole Dance -- the departments under Shop in the main menu -- may not share
 * an order. Every add-to-cart form carries its product's department (data-orbit-department
 * on product-form-component, from snippets/orbit-department.liquid). When the cart already
 * holds another department, the add is stopped before Horizon sees it and the notice from
 * snippets/orbit-cart-guard.liquid asks first: "Esvaziar e adicionar" empties the cart and
 * replays the add, "Manter carrinho" (or Esc, or a click outside) drops it.
 *
 * Horizon binds its handlers on `document` in the capture phase (assets/component.js), so
 * this listens on `window`, which the capture phase reaches first. The add button's click
 * is guarded as well as the form's submit, because Horizon starts its "added" and
 * fly-to-cart animations on the click.
 *
 * What the cart holds comes from the server: inline at page load, then from
 * sections/orbit-cart-departments.liquid after every cart change, when the page comes back
 * into view, and once more just before the notice would open.
 */

const dialog = document.querySelector('[data-orbit-cart-guard]');
const dataElement = document.getElementById('OrbitCartGuardData');

if (dialog instanceof HTMLDialogElement && dataElement) {
  /** @type {{departments?: Record<string, string>, cart?: {lines?: Array<[string, number]>}, section?: string}} */
  let data = {};
  try {
    data = JSON.parse(dataElement.textContent || '{}');
  } catch {
    data = {};
  }

  const names = data.departments || {};
  const sectionId = data.section || 'orbit-cart-departments';
  const root = window.Shopify?.routes?.root || '/';

  /** @type {Array<[string, number]>} */
  let lines = Array.isArray(data.cart?.lines) ? data.cart.lines : [];

  /** While true, the guard lets an add through (the replay after the cart was emptied). */
  let bypass = false;

  /** The add to replay once the customer agrees to empty the cart. @type {(() => void) | null} */
  let pendingAdd = null;

  const body = /** @type {HTMLElement} */ (dialog.querySelector('.orbit-cart-guard__body'));
  const summary = /** @type {HTMLElement} */ (dialog.querySelector('.orbit-cart-guard__summary'));
  const error = /** @type {HTMLElement} */ (dialog.querySelector('[data-orbit-cart-guard-error]'));
  const confirmButton = /** @type {HTMLButtonElement} */ (dialog.querySelector('[data-orbit-cart-guard-confirm]'));
  const cancelButton = /** @type {HTMLButtonElement} */ (dialog.querySelector('[data-orbit-cart-guard-cancel]'));
  const confirmLabel = confirmButton.textContent;

  /** Units in the cart per department. */
  const tally = () => {
    /** @type {Record<string, number>} */
    const totals = {};
    for (const [department, quantity] of lines) {
      if (department) totals[department] = (totals[department] || 0) + Number(quantity || 0);
    }
    return totals;
  };

  /** The departments in the cart that an add from `department` would mix with. */
  const conflictsWith = (/** @type {string} */ department) => {
    if (!department) return [];
    const totals = tally();
    return Object.keys(totals).filter((key) => key !== department && totals[key] > 0);
  };

  async function refresh() {
    try {
      const response = await fetch(`${root}?section_id=${encodeURIComponent(sectionId)}`, {
        credentials: 'same-origin',
        cache: 'no-store',
      });
      if (!response.ok) return;
      const html = await response.text();
      const match = html.match(/<script[^>]*data-orbit-cart-departments[^>]*>([\s\S]*?)<\/script>/);
      if (!match) return;
      const parsed = JSON.parse(match[1]);
      if (Array.isArray(parsed.lines)) lines = parsed.lines;
    } catch {
      // Keep the last known cart; the next change or visit refreshes it.
    }
  }

  // Every cart change in Horizon (adds, quantity changes, removals) announces itself with
  // this event; its promise settles once the server has applied the change.
  document.addEventListener('shopify:cart:lines-update', (event) => {
    const settled = /** @type {any} */ (event).promise;
    Promise.resolve(settled).then(refresh, refresh);
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refresh();
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refresh();
  });

  const departmentOf = (/** @type {Element} */ node) =>
    node.closest('product-form-component')?.getAttribute('data-orbit-department') || '';

  const run = (/** @type {() => void} */ add) => {
    bypass = true;
    try {
      add();
    } finally {
      bypass = false;
    }
  };

  const fill = (/** @type {string} */ department, /** @type {string[]} */ inCart) => {
    const totals = tally();
    const cartNames = inCart.map((key) => names[key] || key);
    const cartLabel =
      cartNames.length > 1 ? `${cartNames.slice(0, -1).join(', ')} e ${cartNames[cartNames.length - 1]}` : cartNames[0];
    const count = inCart.reduce((sum, key) => sum + (totals[key] || 0), 0);
    const items = `${count} ${count === 1 ? 'item' : 'itens'}`;
    const fillIn = (/** @type {string} */ template) =>
      template
        .split('[carrinho]')
        .join(cartLabel)
        .split('[novo]')
        .join(names[department] || department)
        .split('[itens]')
        .join(items);

    body.textContent = fillIn(body.dataset.template || '');
    summary.textContent = fillIn(summary.dataset.template || '');
    summary.hidden = !summary.textContent.trim();
  };

  const setBusy = (/** @type {boolean} */ busy) => {
    confirmButton.disabled = busy;
    cancelButton.disabled = busy;
    confirmButton.setAttribute('aria-busy', String(busy));
    confirmButton.textContent = busy ? confirmButton.dataset.busyLabel || confirmLabel : confirmLabel;
  };

  async function askThenAdd(/** @type {string} */ department, /** @type {() => void} */ add) {
    if (dialog.open) return;

    // The cart may have changed in another tab since the page last looked.
    await refresh();
    const inCart = conflictsWith(department);
    if (!inCart.length) {
      run(add);
      return;
    }

    pendingAdd = add;
    error.hidden = true;
    fill(department, inCart);
    dialog.showModal();
  }

  /**
   * Stops an add that would mix departments and asks first.
   * @param {Event} event
   * @param {Element} node - the add button or the form
   * @param {() => void} replay - repeats the add once the cart is empty
   */
  const guard = (event, node, replay) => {
    if (bypass) return;
    const department = departmentOf(node);
    if (!conflictsWith(department).length) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    askThenAdd(department, replay);
  };

  window.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const button = target?.closest('button[type="submit"]');
      if (!(button instanceof HTMLButtonElement) || button.disabled) return;
      if (!button.closest('product-form-component')) return;
      // An invalid form is the browser's to report; Horizon does not add it either.
      if (button.form && !button.form.checkValidity()) return;

      guard(event, button, () => button.click());
    },
    { capture: true }
  );

  window.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.closest('product-form-component')) return;
      const submitter = event.submitter instanceof HTMLElement ? event.submitter : undefined;

      guard(event, form, () => form.requestSubmit(submitter));
    },
    { capture: true }
  );

  confirmButton.addEventListener('click', async () => {
    const add = pendingAdd;
    if (!add) return;

    setBusy(true);
    error.hidden = true;
    try {
      const response = await fetch(`${root}cart/clear.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`cart/clear ${response.status}`);
    } catch {
      setBusy(false);
      error.hidden = false;
      return;
    }

    lines = [];
    pendingAdd = null;
    setBusy(false);
    dialog.close('confirm');
    run(add);
  });

  cancelButton.addEventListener('click', () => dialog.close('cancel'));

  // A click on the backdrop keeps the cart, like Esc does.
  dialog.addEventListener('click', (event) => {
    if (event.target !== dialog) return;
    const box = dialog.getBoundingClientRect();
    const inside =
      event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
    if (!inside) dialog.close('cancel');
  });

  // Esc keeps the cart too -- except while it is being emptied, when the add has to follow.
  dialog.addEventListener('cancel', (event) => {
    if (confirmButton.disabled) event.preventDefault();
  });

  dialog.addEventListener('close', () => {
    pendingAdd = null;
    setBusy(false);
  });
}
